// Optimizador de menús. Catering → Operación → Optimizador de menús.
// Propone un ciclo balanceado por food cost objetivo, con rotación (no repetir
// dentro de K días) y variedad, usando el costo REAL de las recetas. El usuario
// revisa la propuesta y la APLICA al ciclo (reemplaza su menú).
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Wand2, Plus, Trash2, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { fieldCls } from '@/features/inventario/shared';
import { cat, catRpc, money, SERVICIO_LABEL, SERVICIOS, DIAS } from './shared';

interface Ciclo { id: string; nombre: string }
interface SrvSpec { servicio: string; items: string; objetivo: string; categoria: string }
interface Item { receta_id: string; nombre: string; categoria: string; costo: number }
interface GridRow { dia: number; servicio: string; items: Item[]; costo: number; objetivo: number; desvio: number | null }
interface Resultado {
  dias: number; rotacion_dias: number; grid: GridRow[];
  resumen: { por_servicio: { servicio: string; costo_prom: number; costo_min: number; costo_max: number; desviacion: number }[] };
  variedad: { recetas_usadas: number; platos_pool: number; slots: number };
}

export function OptimizadorCatering() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [dias, setDias] = useState('7');
  const [rot, setRot] = useState('4');
  const [specs, setSpecs] = useState<SrvSpec[]>([{ servicio: 'almuerzo_1', items: '1', objetivo: '0.70', categoria: '' }]);
  const [res, setRes] = useState<Resultado | null>(null);
  const [cicloId, setCicloId] = useState('');

  const { data: ciclos = [] } = useQuery<Ciclo[]>({ queryKey: ['cat-ciclos-opt'], queryFn: () => cat.list<Ciclo>('ciclos', '?select=id,nombre&deleted_at=is.null&order=orden') });

  const params = useMemo(() => ({
    dias: parseInt(dias || '7', 10), rotacion_dias: parseInt(rot || '3', 10),
    servicios: specs.map((s) => ({ servicio: s.servicio, items: parseInt(s.items || '1', 10), objetivo_costo: parseFloat(s.objetivo || '0') || 0, categoria: s.categoria || null })),
  }), [dias, rot, specs]);

  const optimizar = useMutation({
    mutationFn: () => catRpc<Resultado>('cat_menu_optimizar', { _params: params }),
    onSuccess: (r) => setRes(r), onError: (e) => toast.error(getErrorMessage(e)),
  });
  const aplicar = useMutation({
    mutationFn: () => catRpc<{ items: number }>('cat_menu_aplicar', { _ciclo_id: cicloId, _grid: res?.grid ?? [] }),
    onSuccess: (r) => { toast.success(`Menú aplicado al ciclo (${r.items} ítems)`); void qc.invalidateQueries(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const setSpec = (i: number, k: keyof SrvSpec, v: string) => setSpecs((ss) => ss.map((s, j) => j === i ? { ...s, [k]: v } : s));
  // agrupa el grid por servicio para mostrar una fila por día
  const porServicio = useMemo(() => {
    const map: Record<string, GridRow[]> = {};
    (res?.grid ?? []).forEach((g) => { (map[g.servicio] ??= []).push(g); });
    return map;
  }, [res]);

  return (
    <div className="space-y-4 p-4">
      {dlg.ui}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Wand2 className="h-4 w-4" /> Optimizador de menús cíclicos</CardTitle>
          <p className="pt-1 text-xs text-muted-foreground">Arma un ciclo balanceado por food cost objetivo, con rotación y variedad, usando el costo real de las recetas. Propón, revisa y aplica al ciclo.</p></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div><label className="block text-xs text-muted-foreground">Días del ciclo</label><input className={`${fieldCls} w-24`} type="number" value={dias} onChange={(e) => setDias(e.target.value)} /></div>
            <div><label className="block text-xs text-muted-foreground">Rotación (no repetir en N días)</label><input className={`${fieldCls} w-24`} type="number" value={rot} onChange={(e) => setRot(e.target.value)} /></div>
          </div>
          <p className="text-sm font-medium">Servicios a llenar</p>
          {specs.map((s, i) => (
            <div key={i} className="flex flex-wrap items-end gap-1">
              <select className={`${fieldCls} w-28`} value={s.servicio} onChange={(e) => setSpec(i, 'servicio', e.target.value)}>{SERVICIOS.map((x) => <option key={x} value={x}>{SERVICIO_LABEL[x] ?? x}</option>)}</select>
              <div><label className="block text-[10px] text-muted-foreground">ítems/día</label><input className={`${fieldCls} w-16`} type="number" value={s.items} onChange={(e) => setSpec(i, 'items', e.target.value)} /></div>
              <div><label className="block text-[10px] text-muted-foreground">costo objetivo</label><input className={`${fieldCls} w-24`} type="number" step="0.01" value={s.objetivo} onChange={(e) => setSpec(i, 'objetivo', e.target.value)} /></div>
              <div><label className="block text-[10px] text-muted-foreground">categoría (opc.)</label><input className={`${fieldCls} w-28`} placeholder="todas" value={s.categoria} onChange={(e) => setSpec(i, 'categoria', e.target.value)} /></div>
              <button className="pb-2 text-red-600" onClick={() => setSpecs((ss) => ss.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <Button size="sm" variant="ghost" onClick={() => setSpecs((ss) => [...ss, { servicio: 'almuerzo_1', items: '1', objetivo: '0.70', categoria: '' }])}><Plus className="mr-1 h-3.5 w-3.5" /> Servicio</Button>
          <div><Button disabled={optimizar.isPending} onClick={() => optimizar.mutate()}><Wand2 className="mr-1 h-4 w-4" /> Optimizar</Button></div>
        </CardContent>
      </Card>

      {res && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Propuesta de ciclo ({res.dias} días · rotación {res.rotacion_dias})</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {/* Resumen por servicio */}
            <div className="flex flex-wrap gap-2">
              {res.resumen.por_servicio.map((r) => (
                <div key={r.servicio} className="rounded-md border p-2 text-sm">
                  <p className="font-medium">{SERVICIO_LABEL[r.servicio] ?? r.servicio}</p>
                  <p className="text-xs text-muted-foreground">costo prom {money(r.costo_prom, 4)} · rango {money(r.costo_min, 2)}–{money(r.costo_max, 2)} · desv. <span className={r.desviacion > 0.5 ? 'text-amber-600' : 'text-green-700 dark:text-green-400'}>{money(r.desviacion, 2)}</span></p>
                </div>
              ))}
              <div className="rounded-md border p-2 text-sm"><p className="font-medium">Variedad</p><p className="text-xs text-muted-foreground">{res.variedad.recetas_usadas} recetas de {res.variedad.platos_pool} en {res.variedad.slots} slots</p></div>
            </div>

            {/* Grid por servicio */}
            {Object.entries(porServicio).map(([srv, rows]) => (
              <div key={srv} className="overflow-x-auto">
                <p className="pb-1 text-sm font-medium">{SERVICIO_LABEL[srv] ?? srv}</p>
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1 pr-2">Día</th><th className="py-1 pr-2">Menú propuesto</th><th className="py-1 pr-2 text-right">Costo</th><th className="py-1 pr-2 text-right">vs objetivo</th></tr></thead>
                  <tbody>
                    {rows.map((g) => (
                      <tr key={g.dia} className="border-b last:border-0">
                        <td className="py-1 pr-2 font-medium">{DIAS[g.dia - 1] ?? g.dia}</td>
                        <td className="py-1 pr-2">{g.items.map((it) => it.nombre.replace('Plato fuerte — ', '')).join(', ')}</td>
                        <td className="py-1 pr-2 text-right font-mono">{money(g.costo, 4)}</td>
                        <td className={`py-1 pr-2 text-right font-mono ${g.desvio != null && Math.abs(g.desvio) > 0.2 ? 'text-amber-600' : ''}`}>{g.desvio != null ? `${g.desvio > 0 ? '+' : ''}${money(g.desvio, 2)}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

            {/* Aplicar */}
            <div className="flex flex-wrap items-center gap-2 border-t pt-3">
              <select className={`${fieldCls} w-56`} value={cicloId} onChange={(e) => setCicloId(e.target.value)}>
                <option value="">Aplicar al ciclo…</option>
                {ciclos.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
              <Button disabled={!cicloId || aplicar.isPending} onClick={async () => { if (await dlg.confirmar('¿Continuar?', { detalle: 'Esto reemplaza el menú del ciclo seleccionado.' })) aplicar.mutate(); }}>
                <CheckCheck className="mr-1 h-4 w-4" /> Aplicar al ciclo
              </Button>
              <span className="text-xs text-muted-foreground">Reemplaza el menú actual del ciclo con esta propuesta. Revisa antes de aplicar.</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
